/**
 * Peek Room Page - のぞきルーム
 * View-only room with limited chat capabilities (secret messages only)
 *
 * Authentication required: User must be logged in to access this page
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { usePoints } from '../hooks/usePoints';
import { apiClient, SessionExpiredError } from '../lib/api-client';
import { HLSPlayer } from '../lib/hls-player';

// SessionExpiredErrorのチェック（instanceof がモジュール間で動作しない場合のため）
function isSessionExpiredError(err: unknown): boolean {
    return err instanceof SessionExpiredError ||
           (err instanceof Error && err.name === 'SessionExpiredError');
}
import { usePartyCount } from '../hooks/usePartyCount';
import { useSubtitles } from '../hooks/useSubtitles';
import type { PlaybackStatus } from '../lib/types';
import { ROUTES, VIEWER_HEARTBEAT_INTERVAL_MS } from '../lib/constants';
import { AnalyticsLogger } from '../lib/analytics';

import ViewerHeader from '../components/ViewerHeader';
import PointsPurchaseModal from '../components/PointsPurchaseModal';
import MyPageModal from '../components/MyPageModal';
import FavoritesModal from '../components/FavoritesModal';
import NotificationsModal from '../components/NotificationsModal';
import PeekRoomView from '../components/Watch/PeekRoomView';

const POINT_CONSUMPTION_INTERVAL = 60000; // 1 min

export default function PeekRoom() {
    const { t, i18n } = useTranslation('watch');
    const isEnglishMode = i18n.language === 'en';
    const { streamerId } = useParams<{ streamerId: string }>();
    const navigate = useNavigate();
    const { user, logout, isViewer, isAdminOrManager, isLoading } = useAuth();
    const { points, consumeForWatch, purchasePoints, refreshBalance } = usePoints();

    // Check if user is authenticated - redirect if not
    const isAuthenticated = !!user;

    // Handler for login button
    const handleLoginClick = useCallback(() => {
        navigate(ROUTES.LOGIN);
    }, [navigate]);

    // Handler for register button
    const handleRegisterClick = useCallback(() => {
        navigate(ROUTES.REGISTER);
    }, [navigate]);

    // Redirect to login if not authenticated (wait for loading to complete first)
    useEffect(() => {
        if (!isLoading && !isAuthenticated) {
            navigate(ROUTES.LOGIN);
        }
    }, [isLoading, isAuthenticated, navigate]);


    // State
    const [streamKey, setStreamKey] = useState<string | null>(null);
    const [scheduleId, setScheduleId] = useState<string | undefined>(undefined);
    const [streamerName, setStreamerName] = useState<string>(t('list.defaultStreamer'));
    const [showStreamEndedModal, setShowStreamEndedModal] = useState(false);
    const [broadcastStatus, setBroadcastStatus] = useState<'live' | 'paused' | 'suspended'>('live');
    const [suspendedCountdown, setSuspendedCountdown] = useState<number | null>(null);

    // Player State
    const [status, setStatus] = useState<PlaybackStatus>('stopped');
    const videoRef = useRef<HTMLVideoElement>(null);
    const hlsPlayerRef = useRef<HLSPlayer | null>(null);

    // Points & Heartbeat State
    const [pointsError, setPointsError] = useState<string | null>(null);
    const [isPointsModalOpen, setIsPointsModalOpen] = useState(false);
    const [isMyPageModalOpen, setIsMyPageModalOpen] = useState(false);
    const [isFavoritesModalOpen, setIsFavoritesModalOpen] = useState(false);
    const [isNotificationsModalOpen, setIsNotificationsModalOpen] = useState(false);
    const [pointsPurchaseCount, setPointsPurchaseCount] = useState(0);
    const [handleName, setHandleName] = useState<string | null>(null);
    const [unreadNotificationCount, setUnreadNotificationCount] = useState(0);
    const heartbeatRef = useRef<number | null>(null);
    const hasActiveHeartbeatRef = useRef(false);
    const consumptionTimerRef = useRef<number | null>(null);
    const streamStatusCheckRef = useRef<number | null>(null);
    const broadcastStatusRef = useRef<'live' | 'paused' | 'suspended'>('live');
    const suspendedTimerRef = useRef<number | null>(null);
    const showStreamEndedModalRef = useRef(false);
    // プレイヤー再起動トリガー（paused/suspendedからliveへの遷移時にインクリメント）
    const [playerRestartTrigger, setPlayerRestartTrigger] = useState(0);
    // バッファリング中フラグ（課金スキップ用）
    const [, setIsBuffering] = useState(false);
    const isBufferingRef = useRef(false);
    // 視聴開始時刻を追跡（分析用）
    const viewStartTimeRef = useRef<number | null>(null);

    // Counts
    const { partyCount, peekCount } = usePartyCount(streamKey || '');

    // Subtitle State (英語モードのみ有効)
    const [subtitlesEnabled, setSubtitlesEnabled] = useState(false);
    const {
        currentSubtitle,
        subtitles: subtitleHistory,
        isConnected: isSubtitleConnected,
        error: subtitleError,
    } = useSubtitles({
        streamKey: streamKey || '',
        targetLanguage: 'en',  // 日本語→英語翻訳
        enabled: isEnglishMode && subtitlesEnabled && !!streamKey,
        autoReconnect: true,
    });

    const handleToggleSubtitles = useCallback(() => {
        setSubtitlesEnabled(prev => !prev);
    }, []);

    // 分析イベント: 視聴開始・終了を追跡
    useEffect(() => {
        if (!streamKey || !streamerId) return;

        // 視聴開始を記録
        viewStartTimeRef.current = Date.now();
        AnalyticsLogger.getInstance().trackStreamViewStart(streamKey, streamerId);

        return () => {
            // 視聴終了を記録（視聴時間を計算）
            if (viewStartTimeRef.current) {
                const watchDurationSeconds = Math.floor((Date.now() - viewStartTimeRef.current) / 1000);
                AnalyticsLogger.getInstance().trackStreamViewEnd(streamKey, streamerId, watchDurationSeconds);
                viewStartTimeRef.current = null;
            }
        };
    }, [streamKey, streamerId]);

    // 1. Fetch Streamer Profile (once)
    useEffect(() => {
        if (!streamerId) return;

        const fetchProfile = async () => {
            try {
                const profile = await apiClient.getStreamerProfile(streamerId);
                setStreamerName(profile.display_name || profile.username || streamerId);
            } catch (e) {
                console.error('Failed to fetch profile', e);
                setStreamerName(streamerId);
            }
        };

        fetchProfile();
    }, [streamerId]);

    // 1.0.1. Fetch Viewer's handle_name
    useEffect(() => {
        if (!user) return;

        const fetchViewerProfile = async () => {
            try {
                const profile = await apiClient.getMyProfile();
                setHandleName(profile.handle_name);
            } catch (e) {
                console.error('Failed to fetch viewer profile', e);
            }
        };

        fetchViewerProfile();
    }, [user]);

    // Fetch unread notification count
    const fetchUnreadNotificationCount = useCallback(async () => {
        if (!user) {
            setUnreadNotificationCount(0);
            return;
        }
        try {
            const response = await apiClient.getUnreadNotificationCount();
            setUnreadNotificationCount(response.unread_count);
        } catch (e) {
            console.error('Failed to fetch unread notification count', e);
        }
    }, [user]);

    useEffect(() => {
        fetchUnreadNotificationCount();
    }, [fetchUnreadNotificationCount]);

    // Handle notifications modal close - refresh unread count since modal marks them as read
    const handleNotificationsModalClose = useCallback(() => {
        setIsNotificationsModalOpen(false);
        fetchUnreadNotificationCount();
    }, [fetchUnreadNotificationCount]);

    // Sync showStreamEndedModal state with ref for closure access
    useEffect(() => {
        showStreamEndedModalRef.current = showStreamEndedModal;
    }, [showStreamEndedModal]);

    // 1.1. Check stream status and get stream key
    useEffect(() => {
        if (!streamerId) return;

        const checkStreamStatus = async () => {
            try {
                const streams = await apiClient.getActiveStreams();
                const stream = streams.streams.find(s => s.username === streamerId);

                if (stream) {
                    // ストリームが見つかった場合、「配信終了」モーダルが表示されていれば自動的に閉じる
                    // これにより、配信者が再ログインした際に視聴者が自動的に再接続できる
                    if (showStreamEndedModalRef.current) {
                        console.log('[Stream] Stream resumed - automatically closing stream ended modal');
                        setShowStreamEndedModal(false);
                    }

                    setStreamKey(stream.stream_key);
                    setScheduleId(stream.schedule_id ?? undefined);
                    const newStatus = (stream.broadcast_status as 'live' | 'paused' | 'suspended') || 'live';
                    const prevStatus = broadcastStatusRef.current;
                    setBroadcastStatus(newStatus);
                    broadcastStatusRef.current = newStatus;

                    // Handle status transitions
                    if (newStatus === 'suspended' && prevStatus !== 'suspended') {
                        // Start 60-second countdown when stream becomes suspended
                        setSuspendedCountdown(60);
                    } else if (newStatus !== 'suspended' && prevStatus === 'suspended') {
                        // Clear countdown when stream resumes
                        setSuspendedCountdown(null);
                        if (suspendedTimerRef.current) {
                            clearInterval(suspendedTimerRef.current);
                            suspendedTimerRef.current = null;
                        }
                    }

                    // paused/suspendedからliveへの遷移時にHLSプレイヤーを再起動
                    // これにより、配信者が再ログインした際に視聴者側で自動的に映像が再開する
                    if (newStatus === 'live' && (prevStatus === 'paused' || prevStatus === 'suspended')) {
                        console.log('[Stream] Status changed from', prevStatus, 'to live - restarting HLS player');
                        setPlayerRestartTrigger(prev => prev + 1);
                    }
                } else {
                    // Stream ended
                    setStreamKey(null);
                    setShowStreamEndedModal(true);

                    // Stop all timers
                    if (heartbeatRef.current) clearInterval(heartbeatRef.current);
                    if (consumptionTimerRef.current) clearInterval(consumptionTimerRef.current);
                    if (streamStatusCheckRef.current) clearInterval(streamStatusCheckRef.current);

                    // Clear viewer presence
                    if (hasActiveHeartbeatRef.current && streamKey) {
                        try {
                            await apiClient.clearViewerHeartbeat(streamKey, { keepalive: false });
                            hasActiveHeartbeatRef.current = false;
                        } catch (err) {
                            console.error('Failed to clear viewer presence:', err);
                        }
                    }
                }
            } catch (e) {
                console.error('Failed to fetch stream status', e);
            }
        };

        // Initial check
        checkStreamStatus();

        // Poll every 3 seconds (reduced from 5s for faster stream restart detection)
        streamStatusCheckRef.current = window.setInterval(checkStreamStatus, 3000);

        return () => {
            if (streamStatusCheckRef.current) {
                clearInterval(streamStatusCheckRef.current);
                streamStatusCheckRef.current = null;
            }
        };
    }, [streamerId, streamKey]);

    // Handle stream ended modal close - return to before-entry
    const handleStreamEndedClose = () => {
        setShowStreamEndedModal(false);
        navigate(`/watch/${streamerId}/before-entry`);
    };

    const handleExit = () => {
        if (isAdminOrManager()) {
            navigate(ROUTES.CONSOLE);
        } else {
            navigate(`/watch/${streamerId}/before-entry`);
        }
    };

    const handleEnterLiveRoom = () => {
        navigate(`/watch/${streamerId}/live-room`);
    };

    // 3. Player Management with retry logic
    // playerRestartTriggerが変わると、HLSプレイヤーが再初期化される（paused→live遷移時など）
    useEffect(() => {
        if (streamKey && videoRef.current) {
            let retryCount = 0;
            const maxRetries = 5;  // リトライ回数を増やす（ストリーム再開時にマニフェストがすぐに利用可能にならない場合があるため）
            const retryDelay = 2000; // 2秒後にリトライ
            let retryTimeoutId: number | null = null;
            let currentPlayer: HLSPlayer | null = null;
            let isCancelled = false;

            const scheduleRetry = () => {
                if (!isCancelled && retryCount < maxRetries) {
                    retryCount++;
                    console.log(`[HLS] Scheduling retry ${retryCount}/${maxRetries} in ${retryDelay}ms...`);
                    retryTimeoutId = window.setTimeout(startPlayer, retryDelay);
                } else if (!isCancelled) {
                    console.error('[HLS] Max retries reached, giving up');
                    setStatus('error');
                }
            };

            const startPlayer = async () => {
                if (isCancelled || !videoRef.current) return;

                // 前のプレーヤーがあれば破棄
                if (currentPlayer) {
                    currentPlayer.destroy();
                }

                console.log('[HLS] Starting player with streamKey:', streamKey, 'trigger:', playerRestartTrigger, 'attempt:', retryCount + 1);
                currentPlayer = new HLSPlayer(videoRef.current, {
                    streamKey,
                    serverType: 'ome',
                    onStatusChange: (newStatus) => {
                        setStatus(newStatus);
                        // ネットワークエラー時にリトライをスケジュール
                        if (newStatus === 'error') {
                            console.log('[HLS] Error status received, scheduling retry');
                            scheduleRetry();
                        }
                    },
                    onError: (error) => {
                        console.error('[HLS] Player error:', error.message);
                        // エラー発生時にリトライをスケジュール（ただしstatus変更で既にスケジュールされていれば重複しない）
                    },
                    onBufferingChange: (buffering) => {
                        console.log('[HLS] Buffering state changed:', buffering);
                        setIsBuffering(buffering);
                        isBufferingRef.current = buffering;
                    },
                }, true); // Enable low latency mode
                hlsPlayerRef.current = currentPlayer;

                try {
                    await currentPlayer.start();
                } catch (error) {
                    console.error(`[HLS] Failed to start player:`, error);
                    scheduleRetry();
                }
            };

            startPlayer();

            return () => {
                isCancelled = true;
                if (retryTimeoutId) {
                    clearTimeout(retryTimeoutId);
                }
                if (currentPlayer) {
                    currentPlayer.destroy();
                }
                hlsPlayerRef.current = null;
            };
        }
    }, [streamKey, playerRestartTrigger]);

    // 4. Heartbeat & Points Consumption
    const startViewerHeartbeat = useCallback(async () => {
        if (!streamKey || !isViewer()) return;

        try {
            await apiClient.sendViewerHeartbeat(streamKey, 'peek');
            hasActiveHeartbeatRef.current = true;

            if (heartbeatRef.current) clearInterval(heartbeatRef.current);
            heartbeatRef.current = window.setInterval(async () => {
                try {
                    await apiClient.sendViewerHeartbeat(streamKey, 'peek');
                } catch (err) {
                    console.error('Heartbeat failed:', err);
                }
            }, VIEWER_HEARTBEAT_INTERVAL_MS);
        } catch (err) {
            console.error('Failed to start heartbeat:', err);
        }
    }, [streamKey, user?.role]);

    const clearViewerPresence = useCallback(async () => {
        // Note: Don't check isViewer() here - we need to clear presence even after logout
        if (!streamKey || !hasActiveHeartbeatRef.current) return;

        try {
            await apiClient.clearViewerHeartbeat(streamKey, { keepalive: false });
            hasActiveHeartbeatRef.current = false;
        } catch (err) {
            console.error('Failed to clear viewer presence:', err);
        }
    }, [streamKey]);

    // Ref to prevent double point consumption on mount (React StrictMode)
    const pointConsumptionStartedRef = useRef(false);

    const startPointConsumption = useCallback(async () => {
        // Prevent double execution (React StrictMode protection)
        if (pointConsumptionStartedRef.current) {
            return;
        }
        pointConsumptionStartedRef.current = true;

        if (consumptionTimerRef.current) clearInterval(consumptionTimerRef.current);

        // Initial point consumption on room entry (only if broadcast is live)
        try {
            if (streamKey && broadcastStatusRef.current === 'live') {
                await consumeForWatch(streamKey, 'peek');
            }
        } catch (err) {
            // セッション切れの場合はpointsErrorを設定しない（handleSessionExpiredで処理済み）
            if (isSessionExpiredError(err)) {
                console.log('Session expired during point consumption');
                return;
            }
            setPointsError(t('errors.insufficientPoints'));
            console.error('Initial point consumption failed:', err);
            pointConsumptionStartedRef.current = false;  // Reset on error
            return;
        }

        // Start periodic consumption every minute
        consumptionTimerRef.current = window.setInterval(async () => {
            try {
                // Skip point consumption if broadcast is paused or suspended
                if (broadcastStatusRef.current === 'paused') {
                    console.log('[Points] Skipping consumption - broadcast is paused');
                    return;
                }
                if (broadcastStatusRef.current === 'suspended') {
                    console.log('[Points] Skipping consumption - broadcast is suspended');
                    return;
                }
                // Skip point consumption if buffering (video is not playing)
                if (isBufferingRef.current) {
                    console.log('[Points] Skipping consumption - buffering in progress');
                    return;
                }
                if (streamKey) {
                    await consumeForWatch(streamKey, 'peek');
                }
            } catch (err) {
                // セッション切れの場合はpointsErrorを設定しない
                if (isSessionExpiredError(err)) {
                    console.log('Session expired during point consumption');
                    return;
                }
                setPointsError(t('errors.insufficientPoints'));
                console.error('Point consumption failed:', err);
            }
        }, POINT_CONSUMPTION_INTERVAL);
    }, [consumeForWatch, streamKey, t]);

    // Manage Heartbeat/Points Lifecycle
    // Wait for auth to complete before starting point consumption
    useEffect(() => {
        if (streamKey && !isLoading && isViewer()) {
            startViewerHeartbeat();
            startPointConsumption();
        }

        return () => {
            if (heartbeatRef.current) clearInterval(heartbeatRef.current);
            if (consumptionTimerRef.current) clearInterval(consumptionTimerRef.current);
            clearViewerPresence();
        };
    }, [streamKey, isLoading, isViewer, startViewerHeartbeat, startPointConsumption, clearViewerPresence]);

    // Handle suspended countdown - auto-exit when countdown reaches 0
    useEffect(() => {
        if (suspendedCountdown === null) {
            if (suspendedTimerRef.current) {
                clearInterval(suspendedTimerRef.current);
                suspendedTimerRef.current = null;
            }
            return;
        }

        // Start countdown interval
        suspendedTimerRef.current = window.setInterval(() => {
            setSuspendedCountdown((prev) => {
                if (prev === null || prev <= 1) {
                    // Countdown finished - auto-exit
                    if (suspendedTimerRef.current) {
                        clearInterval(suspendedTimerRef.current);
                        suspendedTimerRef.current = null;
                    }
                    // Show stream ended modal and navigate
                    setShowStreamEndedModal(true);
                    return null;
                }
                return prev - 1;
            });
        }, 1000);

        return () => {
            if (suspendedTimerRef.current) {
                clearInterval(suspendedTimerRef.current);
                suspendedTimerRef.current = null;
            }
        };
    }, [suspendedCountdown === null ? null : 'active']); // Only re-run when countdown starts/stops

    // Stop video and audio playback when points run out
    useEffect(() => {
        if (pointsError && hlsPlayerRef.current) {
            hlsPlayerRef.current.destroy();
            hlsPlayerRef.current = null;
            if (videoRef.current) {
                videoRef.current.pause();
                videoRef.current.srcObject = null;
                videoRef.current.src = '';
                videoRef.current.load();
            }
        }
    }, [pointsError]);

    const handlePurchasePoints = async (amount: 1000 | 3000 | 5000 | 10000 | 30000 | 50000) => {
        await purchasePoints(amount);
        // 分析イベント: ポイント購入成功
        AnalyticsLogger.getInstance().trackPurchase(amount, amount);
        setPointsError(null);
        setPointsPurchaseCount(prev => prev + 1);

        // Resume viewing after point purchase
        if (streamKey && videoRef.current) {
            if (hlsPlayerRef.current) {
                hlsPlayerRef.current.destroy();
                hlsPlayerRef.current = null;
            }

            const player = new HLSPlayer(videoRef.current, {
                streamKey,
                serverType: 'ome',
                onStatusChange: setStatus,
                onBufferingChange: (buffering) => {
                    setIsBuffering(buffering);
                    isBufferingRef.current = buffering;
                },
            }, true);
            hlsPlayerRef.current = player;

            player.start().catch((error) => {
                console.error('Failed to restart HLS player:', error);
                setStatus('error');
            });

            startViewerHeartbeat();

            if (consumptionTimerRef.current) clearInterval(consumptionTimerRef.current);

            // Only consume points if broadcast is live
            if (broadcastStatusRef.current === 'live') {
                try {
                    await consumeForWatch(streamKey, 'peek');
                } catch (err) {
                    if (isSessionExpiredError(err)) {
                        console.log('Session expired during point consumption');
                        return;
                    }
                    setPointsError(t('errors.insufficientPoints'));
                    console.error('Initial point consumption after purchase failed:', err);
                    return;
                }
            }

            consumptionTimerRef.current = window.setInterval(async () => {
                try {
                    // Skip point consumption if broadcast is paused or suspended
                    if (broadcastStatusRef.current === 'paused') {
                        console.log('[Points] Skipping consumption - broadcast is paused');
                        return;
                    }
                    if (broadcastStatusRef.current === 'suspended') {
                        console.log('[Points] Skipping consumption - broadcast is suspended');
                        return;
                    }
                    // Skip point consumption if buffering (video is not playing)
                    if (isBufferingRef.current) {
                        console.log('[Points] Skipping consumption - buffering in progress');
                        return;
                    }
                    await consumeForWatch(streamKey, 'peek');
                } catch (err) {
                    if (isSessionExpiredError(err)) {
                        console.log('Session expired during point consumption');
                        return;
                    }
                    setPointsError(t('errors.insufficientPoints'));
                    console.error('Point consumption failed:', err);
                }
            }, POINT_CONSUMPTION_INTERVAL);
        }
    };

    return (
        <div className="min-h-screen bg-gray-200">
            <ViewerHeader
                points={isAuthenticated ? points : null}
                onPointsClick={() => setIsPointsModalOpen(true)}
                onExitClick={handleExit}
                showExitButton={true}
                exitButtonLabel={isAdminOrManager() ? '管理画面に戻る' : t('header.exitRoom')}
                title={t('header.peekRoomTitle')}
                icon="👁️"
                username={user?.username}
                onLogout={logout}
                onMyPageClick={() => setIsMyPageModalOpen(true)}
                onFavoritesClick={() => setIsFavoritesModalOpen(true)}
                onNotificationsClick={() => setIsNotificationsModalOpen(true)}
                notificationCount={unreadNotificationCount}
                isAuthenticated={isAuthenticated}
                onLoginClick={handleLoginClick}
                onRegisterClick={handleRegisterClick}
                showLanguageSelector={true}
                isAdminOrManager={isAdminOrManager()}
            />

            <div className="max-w-[1600px] mx-auto px-4 py-4">
                <PeekRoomView
                    streamKey={streamKey}
                    streamerId={streamerId || ''}
                    videoRef={videoRef}
                    status={status}
                    broadcastStatus={broadcastStatus}
                    suspendedCountdown={suspendedCountdown}
                    partyCount={partyCount}
                    peekCount={peekCount}
                    viewerName={user?.username || t('header.guest')}
                    pointsError={pointsError}
                    onPurchasePoints={() => setIsPointsModalOpen(true)}
                    onEnterLiveRoom={handleEnterLiveRoom}
                    onPointsUpdate={refreshBalance}
                    pointsPurchaseCount={pointsPurchaseCount}
                    canReport={isViewer()}
                    scheduleId={scheduleId}
                    canModerateChat={isAdminOrManager()}
                    // Subtitle props
                    currentSubtitle={currentSubtitle}
                    subtitleHistory={subtitleHistory}
                    subtitlesEnabled={subtitlesEnabled}
                    onToggleSubtitles={isEnglishMode ? handleToggleSubtitles : undefined}
                    subtitleError={subtitleError}
                    isSubtitleConnecting={isEnglishMode && subtitlesEnabled && !isSubtitleConnected && !subtitleError}
                />
            </div>

            <PointsPurchaseModal
                isOpen={isPointsModalOpen}
                onClose={() => setIsPointsModalOpen(false)}
                currentPoints={points}
                onPurchase={handlePurchasePoints}
            />

            <MyPageModal
                isOpen={isMyPageModalOpen}
                onClose={() => setIsMyPageModalOpen(false)}
                points={points}
                username={user?.username || ''}
                handleName={handleName}
                onPointsClick={() => setIsPointsModalOpen(true)}
                onHandleNameUpdate={(newName) => setHandleName(newName)}
            />

            <FavoritesModal
                isOpen={isFavoritesModalOpen}
                onClose={() => setIsFavoritesModalOpen(false)}
                onViewStreamer={(id) => {
                    setIsFavoritesModalOpen(false);
                    navigate(`/watch/${id}/before-entry`);
                }}
                isInRoom={true}
            />

            <NotificationsModal
                isOpen={isNotificationsModalOpen}
                onClose={handleNotificationsModalClose}
            />

            {/* Stream Ended Modal */}
            {showStreamEndedModal && (
                <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center">
                    <div className="bg-white rounded-lg max-w-md w-[90%] shadow-2xl overflow-hidden border-2 border-gray-200 animate-slide-in-up">
                        {/* Header */}
                        <div className="bg-gradient-to-r from-gray-600 to-gray-700 px-6 py-4 flex items-center gap-3">
                            <div className="w-12 h-12 bg-white rounded-full flex items-center justify-center text-2xl shadow">
                                📺
                            </div>
                            <div className="text-xl font-bold text-white">{t('streamEnded.title')}</div>
                        </div>
                        {/* Content */}
                        <div className="px-6 py-6 text-gray-600 leading-relaxed text-center">
                            <p className="mb-4">
                                {t('streamEnded.message', { streamerName })}
                            </p>
                            <p className="text-sm text-gray-500">
                                {t('streamEnded.thanks')}
                            </p>
                        </div>
                        {/* Footer */}
                        <div className="px-6 py-4 bg-gray-50 flex justify-center">
                            <button
                                onClick={handleStreamEndedClose}
                                className="px-8 py-2.5 bg-gradient-to-r from-pink-500 to-pink-600 text-white font-semibold rounded hover:from-pink-600 hover:to-pink-700 hover:-translate-y-0.5 shadow hover:shadow-lg transition-all"
                            >
                                {t('streamEnded.ok')}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
